import React, { useEffect, useState } from "react";
import ToolRow from "./ToolRow";
import AddToolForm from "./AddToolForm";
import { fetchAllTools, setToolEnabled, addTool, deleteTool } from "../../api/toolAdmin";

const ManageToolsPage = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [addingTool, setAddingTool] = useState(false);
  const [addError, setAddError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    loadTools();
  }, []);

  const loadTools = async () => {
    try {
      setLoading(true);
      const data = await fetchAllTools();
      setTools(data);
      setError("");
    } catch (err) {
      setError("Failed to load tools");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (toolId, enabled) => {
    try {
      await setToolEnabled(toolId, enabled);
      
      setTools(prev =>
        prev.map(tool =>
          tool.id === toolId ? { ...tool, enabled } : tool
        )
      );
    } catch (err) {
      setError(`Failed to update tool: ${err.response?.data?.error || err.message}`);
      console.error(err);
    }
  };

  const handleAddTool = async (formData) => {
    try {
      setAddingTool(true);
      setAddError("");
      const newTool = await addTool(formData);
      
      setTools(prev => [...prev, newTool]);
      setSuccessMessage(`Tool "${newTool.name}" added successfully`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      throw new Error(err.response?.data?.error || "Failed to add tool");
    } finally {
      setAddingTool(false);
    }
  };

  const handleDeleteTool = async (toolId) => {
    try {
      await deleteTool(toolId);
      setTools(prev => prev.filter(tool => tool.id !== toolId));
      setSuccessMessage("Tool deleted successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError(`Failed to delete tool: ${err.response?.data?.error || err.message}`);
      console.error(err);
    }
  };

  // Group tools by category
  const toolsByCategory = tools.reduce((acc, tool) => {
    const category = tool.category || "Other";
    if (!acc[category]) acc[category] = [];
    acc[category].push(tool);
    return acc;
  }, {});

  const existingCategories = Array.from(new Set(tools.map(t => t.category))).filter(Boolean);

  const categoryOrder = [
    "Network Scanning & Enumeration",
    "Subdomain & Recon",
    "Web Vulnerability Scanners",
    "Credential & Auth Auditing",
    "SSL / Security Headers",
    "Vulnerability Intelligence",
    "AWS Security Scanning",
  ];

  const sortedCategories = [
    ...categoryOrder.filter(cat => toolsByCategory[cat]),
    ...Object.keys(toolsByCategory).filter(cat => !categoryOrder.includes(cat)).sort(),
  ];

  if (loading) {
    return <div className="manage-tools-page loading">Loading tools...</div>;
  }

  return (
    <div className="manage-tools-page">
      <div className="manage-tools-header">
        <h1>Manage Scan Tools</h1>
        <p className="manage-tools-description">
          Enable or disable built-in scanning tools, and add custom tools to extend the scanner.
        </p>
      </div>

      {error && <div className="manage-tools-error">{error}</div>}
      {successMessage && <div className="manage-tools-success">{successMessage}</div>}

      <div className="manage-tools-content">
        {/* Add Tool Form */}
        <div className="add-tool-section">
          <AddToolForm
            existingCategories={existingCategories}
            onSubmit={handleAddTool}
            isSubmitting={addingTool}
            error={addError}
            onSuccess={() => setAddError("")}
          />
        </div>

        {/* Tools Management */}
        <div className="tools-management-section">
          <h2>Current Tools ({tools.length})</h2>

          {sortedCategories.map(category => (
            <div key={category} className="tool-category-section">
              <h3 className="manage-tools-category-heading">{category}</h3>
              <div className="manage-tools-list">
                {toolsByCategory[category].map(tool => (
                  <ToolRow
                    key={tool.id}
                    tool={tool}
                    onToggleEnabled={handleToggleEnabled}
                    onDelete={handleDeleteTool}
                    onDeleteConfirm={handleDeleteTool}
                  />
                ))}
              </div>
            </div>
          ))}

          {tools.length === 0 && (
            <div className="manage-tools-empty-state">
              <p>No tools available yet. Add your first custom tool above.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageToolsPage;
